import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { DenyDialog } from 'components/DenyDialog';
import { useSnackbar } from 'notistack';
import theme from 'theme';
import { useAuth } from 'auth';

const ERROR_MESSAGES = {
  approved: 'Error when approving hardware request',
  canceled: 'Error when canceling hardware request',
  completed: 'Error when completing hardware request',
};

const RequestAction = ({
  userId,
  requestId,
  status,
  requestedBy,
  onRequestAction,
  onDenyRequestAction,
  description,
  title,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isOfficeManager } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [note, setNote] = useState('');

  const handleAction = useCallback(
    (e, targetStatus) => {
      e.preventDefault();
      setIsProcessing(true);
      onRequestAction({ requestId, userId, status: targetStatus, requestedBy, description, title })
        .then(() => {
          enqueueSnackbar(`Hardware request successfully ${targetStatus}`, { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar(ERROR_MESSAGES[targetStatus], { variant: 'error' });
        })
        .finally(() => setIsProcessing(false));
    },
    [enqueueSnackbar, onRequestAction, requestId, userId, description, requestedBy, title],
  );

  const onDeny = useCallback(
    (e, addNote) => {
      e.preventDefault();
      setIsProcessing(true);
      onDenyRequestAction({
        requestId,
        userId,
        status: 'denied',
        requestedBy,
        description,
        title,
        note: addNote ? note : '',
      })
        .then(() => {
          enqueueSnackbar('Hardware request successfully denied', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Error when denying hardware request', { variant: 'error' });
        })
        .finally(() => setIsProcessing(false));
    },
    [
      enqueueSnackbar,
      onDenyRequestAction,
      requestId,
      userId,
      description,
      requestedBy,
      title,
      note,
    ],
  );

  const onDenyOpenDialog = e => {
    e.preventDefault();
    setDialogOpen(true);
  };

  return (
    <div>
      {((status === 'pending' && !isOfficeManager) || status === 'approved') && (
        <Button
          variant="contained"
          size="small"
          disabled={isProcessing}
          onClick={e => handleAction(e, status === 'pending' ? 'approved' : 'completed')}
          sx={{
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: '#388e3c',
            },
          }}
          data-cy={status === 'pending' ? 'approveButton' : 'completeButton'}
        >
          {status === 'pending' ? 'Approve' : 'Complete'}
        </Button>
      )}
      {status !== 'completed' && (status === 'approved' || !isOfficeManager) && (
        <Button
          variant="contained"
          size="small"
          disabled={isProcessing}
          onClick={e => (status === 'pending' ? onDenyOpenDialog(e) : handleAction(e, 'canceled'))}
          sx={{
            backgroundColor: theme.palette.danger.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: '#c62828',
            },
            marginLeft: '5px',
          }}
          data-cy={status === 'pending' ? 'denyButton' : 'cancelButton'}
        >
          {status === 'pending' ? 'Deny' : 'Cancel'}
        </Button>
      )}
      <DenyDialog
        setDialogOpen={setDialogOpen}
        type="hardware"
        setNote={setNote}
        onDeny={onDeny}
        isProcessing={isProcessing}
        isDialogOpen={isDialogOpen}
        note={note}
        requestedBy={requestedBy}
      />
    </div>
  );
};

RequestAction.propTypes = {
  userId: PropTypes.string.isRequired,
  requestId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onRequestAction: PropTypes.func.isRequired,
  onDenyRequestAction: PropTypes.func.isRequired,
  description: PropTypes.string,
  title: PropTypes.string,
  requestedBy: PropTypes.any,
};

export default RequestAction;
